/**
 * Workflow Node Tester Dashboard
 * 
 * Interactive testing interface for all workflow nodes
 * - Test each node type with real GPT-5/GPT-5-nano
 * - Verify JSON/text data passing
 * - Monitor database updates
 * - View execution traces
 */

import { useState } from 'react';
import { Brain, Zap, Database, CheckCircle, XCircle, Play, Eye, FileText, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { workflowExecutionEngine, type WorkflowDefinition } from '@/services/workflowExecutionEngine';
import { gpt5NanoAgent } from '@/services/gpt5NanoAgent';
import { blink } from '@/lib/blink';
import { toast } from 'sonner';

interface TestResult {
  nodeType: string;
  nodeName: string;
  status: 'success' | 'failed' | 'running';
  executionTime: number;
  output?: any;
  error?: string;
  aiModel?: string;
  dataFlow?: {
    input: any;
    output: any;
    passedToNext: any;
  };
}

export default function WorkflowNodeTester() {
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedNode, setSelectedNode] = useState<string>('');
  const [testInput, setTestInput] = useState('');
  const [liveOutput, setLiveOutput] = useState<any>(null);

  const nodeTests = [
    {
      id: 'ai_classify',
      name: 'AI Classify Node',
      type: 'ai',
      description: 'Test GPT-5 classification with structured JSON output',
      model: 'gpt-5',
      testInput: 'Classify: Urgent meeting request from CEO tomorrow at 3pm',
      expectedOutput: { category: 'work', priority: 'high', isUrgent: true }
    },
    {
      id: 'ai_extract',
      name: 'AI Extract Node',
      type: 'ai',
      description: 'Test entity extraction with GPT-5',
      model: 'gpt-5',
      testInput: 'Extract: Meeting with Sarah Johnson on Friday at 2pm to discuss Q4 budget',
      expectedOutput: { entities: [{ type: 'person' }, { type: 'date' }, { type: 'action_item' }] }
    },
    {
      id: 'ai_generate',
      name: 'AI Generate Node',
      type: 'ai',
      description: 'Test content generation with GPT-5',
      model: 'gpt-5',
      testInput: 'Generate: Professional email accepting meeting invitation',
      expectedOutput: { generatedText: 'string' }
    },
    {
      id: 'ai_research',
      name: 'AI Research Node',
      type: 'ai',
      description: 'Test web research with GPT-5 and search',
      model: 'gpt-5',
      testInput: 'Research: Latest AI developments in 2025',
      expectedOutput: { research: 'string', sources: [] }
    },
    {
      id: 'ai_analyze',
      name: 'AI Analyze Node',
      type: 'ai',
      description: 'Test advanced analysis with GPT-5',
      model: 'gpt-5',
      testInput: 'Analyze: Customer feedback showing mixed sentiment about new feature',
      expectedOutput: { analysis: 'string', insights: [], recommendations: [] }
    },
    {
      id: 'data_passing',
      name: 'Data Passing Test',
      type: 'workflow',
      description: 'Test JSON/text passing between multiple nodes',
      model: 'gpt-5',
      testInput: 'Multi-node workflow with data dependencies',
      expectedOutput: { nodeChain: true, dataIntegrity: true }
    },
    {
      id: 'gpt5_nano_direct',
      name: 'GPT-5 Nano Agent Direct',
      type: 'agent',
      description: 'Test GPT-5 Nano Agent directly',
      model: 'gpt-5-nano',
      testInput: 'Show me urgent emails from today',
      expectedOutput: { action: {}, reasoning: 'string' }
    }
  ];

  const runSingleNodeTest = async (test: typeof nodeTests[0]) => {
    const startTime = Date.now();
    
    setTestResults(prev => [...prev, {
      nodeType: test.type,
      nodeName: test.name,
      status: 'running',
      executionTime: 0,
      aiModel: test.model
    }]);

    try {
      let result: any;
      const user = await blink.auth.me();

      if (test.type === 'agent') {
        // Test GPT-5 Nano Agent directly
        const response = await gpt5NanoAgent.processUserIntent(test.testInput);
        result = {
          success: true,
          output: {
            action: response.action,
            reasoning: response.reasoning,
            thinking: response.thinking,
            confidence: response.action.confidence
          }
        };
      } else if (test.type === 'workflow') {
        // Test multi-node workflow
        const workflow: WorkflowDefinition = {
          id: `test_${test.id}_${Date.now()}`,
          name: test.name,
          description: test.description,
          nodes: [
            {
              id: 'trigger-1',
              type: 'trigger',
              title: 'Manual Trigger',
              description: 'Test trigger',
              config: { triggerType: 'manual' }
            },
            {
              id: 'ai-1',
              type: 'ai',
              title: 'AI Classify',
              description: 'First node',
              config: {
                model: 'gpt-5',
                prompt: 'Classify: {{trigger.subject}}'
              }
            },
            {
              id: 'ai-2',
              type: 'ai',
              title: 'AI Extract',
              description: 'Second node using first node output',
              config: {
                model: 'gpt-5',
                prompt: 'Based on category {{node_ai-1.category}}, extract key info'
              }
            }
          ],
          enabled: true,
          userId: user.id
        };

        result = await workflowExecutionEngine.executeWorkflow(workflow, {
          userId: user.id,
          trigger: {
            type: 'manual',
            subject: test.testInput
          }
        });

        // Add data flow tracking
        if (result.success && result.results) {
          const dataFlow = {
            input: result.results[0]?.output,
            output: result.results[result.results.length - 1]?.output,
            passedToNext: result.results.map((r: any) => ({
              nodeId: r.nodeId,
              output: r.output
            }))
          };
          result.dataFlow = dataFlow;
        }
      } else {
        // Test single AI node
        const workflow: WorkflowDefinition = {
          id: `test_${test.id}_${Date.now()}`,
          name: test.name,
          description: test.description,
          nodes: [
            {
              id: 'trigger-1',
              type: 'trigger',
              title: 'Manual Trigger',
              description: 'Test trigger',
              config: { triggerType: 'manual' }
            },
            {
              id: 'ai-test-1',
              type: 'ai',
              title: test.name,
              description: test.description,
              config: {
                model: test.model,
                prompt: test.testInput,
                useWebSearch: test.id === 'ai_research'
              }
            }
          ],
          enabled: true,
          userId: user.id
        };

        result = await workflowExecutionEngine.executeWorkflow(workflow, {
          userId: user.id,
          trigger: { type: 'manual' }
        });
      }

      const executionTime = Date.now() - startTime;

      setTestResults(prev => prev.map((r, idx) =>
        idx === prev.length - 1 ? {
          ...r,
          status: result.success ? 'success' : 'failed',
          executionTime,
          output: result.output || result.results?.[1]?.output,
          dataFlow: result.dataFlow,
          error: result.error
        } : r
      ));

      if (result.success) {
        toast.success(`${test.name} completed in ${executionTime}ms`);
      } else {
        toast.error(`${test.name} failed`);
      }

      return result;
    } catch (error) {
      const executionTime = Date.now() - startTime;
      
      setTestResults(prev => prev.map((r, idx) =>
        idx === prev.length - 1 ? {
          ...r,
          status: 'failed',
          executionTime,
          error: error instanceof Error ? error.message : 'Unknown error'
        } : r
      ));

      toast.error(`${test.name} failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    }
  };

  const runAllTests = async () => {
    setIsRunning(true);
    setProgress(0);
    setTestResults([]);

    try {
      for (let i = 0; i < nodeTests.length; i++) {
        const test = nodeTests[i];
        await runSingleNodeTest(test);
        setProgress(((i + 1) / nodeTests.length) * 100);

        // Small delay between tests
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      toast.success('All tests completed!');
    } catch (error) {
      toast.error('Test suite failed');
    } finally {
      setIsRunning(false);
    }
  };

  const runCustomTest = async () => {
    if (!selectedNode || !testInput) {
      toast.error('Please select a node type and provide test input');
      return;
    }

    const test = nodeTests.find(t => t.id === selectedNode);
    if (!test) return;

    try {
      setLiveOutput(null);
      const modifiedTest = { ...test, testInput };
      const result = await runSingleNodeTest(modifiedTest);
      setLiveOutput(result);
    } catch (error) {
      setLiveOutput({ error: error instanceof Error ? error.message : 'Unknown error' });
    }
  };

  const successCount = testResults.filter(r => r.status === 'success').length;
  const failedCount = testResults.filter(r => r.status === 'failed').length;
  const avgExecutionTime = testResults.length > 0
    ? Math.round(testResults.reduce((sum, r) => sum + r.executionTime, 0) / testResults.length)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold">Workflow Node Tester</h1>
            <p className="text-muted-foreground">Test all workflow nodes with real GPT-5/GPT-5-nano integration</p>
          </div>
          <Button onClick={runAllTests} disabled={isRunning} size="lg">
            {isRunning ? <Activity className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            Run All Tests
          </Button>
        </div>

        {/* Progress */}
        {isRunning && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Testing in progress...</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} />
          </div>
        )}

        {/* Stats */}
        {testResults.length > 0 && (
          <div className="grid grid-cols-4 gap-4 mt-4">
            <Card className="p-4">
              <div className="text-sm text-muted-foreground mb-1">Total Tests</div>
              <div className="text-2xl font-bold">{testResults.length}</div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-muted-foreground mb-1">Passed</div>
              <div className="text-2xl font-bold text-green-500">{successCount}</div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-muted-foreground mb-1">Failed</div>
              <div className="text-2xl font-bold text-red-500">{failedCount}</div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-muted-foreground mb-1">Avg Time</div>
              <div className="text-2xl font-bold">{avgExecutionTime}ms</div>
            </Card>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="results">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="results">Test Results</TabsTrigger>
          <TabsTrigger value="custom">Custom Test</TabsTrigger>
          <TabsTrigger value="docs">Documentation</TabsTrigger>
        </TabsList>

        {/* Test Results */}
        <TabsContent value="results" className="space-y-3">
          {testResults.length === 0 ? (
            <Card className="p-12 text-center">
              <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No test results yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Run tests to see results here
              </p>
              <Button onClick={runAllTests}>
                <Play className="w-4 h-4 mr-2" />
                Run All Tests
              </Button>
            </Card>
          ) : (
            testResults.map((result, idx) => (
              <Card key={idx} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`p-2 rounded-lg ${
                      result.status === 'success' ? 'bg-green-500/10' :
                      result.status === 'failed' ? 'bg-red-500/10' :
                      'bg-blue-500/10'
                    }`}>
                      {result.status === 'success' ? <CheckCircle className="w-5 h-5 text-green-500" /> :
                       result.status === 'failed' ? <XCircle className="w-5 h-5 text-red-500" /> :
                       <Activity className="w-5 h-5 text-blue-500 animate-spin" />}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold">{result.nodeName}</h4>
                        <Badge variant="outline">{result.nodeType}</Badge>
                        {result.aiModel && (
                          <Badge variant="secondary">{result.aiModel}</Badge>
                        )}
                      </div>

                      <p className="text-sm text-muted-foreground mb-2">
                        Execution time: {result.executionTime}ms
                      </p>

                      {result.error && (
                        <div className="p-2 bg-red-500/10 rounded text-sm text-red-500 mb-2">
                          {result.error}
                        </div>
                      )}

                      {result.output && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-sm font-medium mb-2">
                            View Output
                          </summary>
                          <pre className="p-2 bg-accent/50 rounded text-xs overflow-x-auto">
                            {JSON.stringify(result.output, null, 2)}
                          </pre>
                        </details>
                      )}

                      {result.dataFlow && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-sm font-medium mb-2">
                            View Data Flow
                          </summary>
                          <pre className="p-2 bg-accent/50 rounded text-xs overflow-x-auto">
                            {JSON.stringify(result.dataFlow, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>

                  <Button variant="ghost" size="sm">
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Custom Test */}
        <TabsContent value="custom">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Custom Node Test</h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Select Node Type</label>
                <select
                  value={selectedNode}
                  onChange={(e) => setSelectedNode(e.target.value)}
                  className="w-full p-2 rounded-lg border bg-background"
                >
                  <option value="">Choose a node type...</option>
                  {nodeTests.map(test => (
                    <option key={test.id} value={test.id}>
                      {test.name} ({test.model})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Test Input</label>
                <Textarea
                  value={testInput}
                  onChange={(e) => setTestInput(e.target.value)}
                  placeholder="Enter test input for the node..."
                  className="min-h-[100px]"
                />
              </div>

              <Button onClick={runCustomTest} className="w-full">
                <Play className="w-4 h-4 mr-2" />
                Run Custom Test
              </Button>

              {liveOutput && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Live Output:</h4>
                  <pre className="p-4 bg-accent/50 rounded text-xs overflow-x-auto">
                    {JSON.stringify(liveOutput, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* Documentation */}
        <TabsContent value="docs">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Node Testing Documentation</h3>
            
            <div className="space-y-6">
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Brain className="w-4 h-4" />
                  AI Nodes (GPT-5/GPT-5-nano)
                </h4>
                <p className="text-sm text-muted-foreground">
                  All AI nodes use real GPT-5 or GPT-5-nano models with structured output validation.
                  Supported node types: AI Classify, AI Extract, AI Generate, AI Research, AI Analyze.
                </p>
              </div>

              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Data Passing
                </h4>
                <p className="text-sm text-muted-foreground">
                  Nodes pass data using JSON format with template variable interpolation.
                  Access previous node outputs using: {'{'}{'{'} node_id.property {'}'}{'}'}
                </p>
              </div>

              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Database className="w-4 h-4" />
                  Database Integration
                </h4>
                <p className="text-sm text-muted-foreground">
                  All workflow executions are automatically saved to the workflow_executions table
                  with complete node results, timing, and error information.
                </p>
              </div>

              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Test Coverage
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>AI Classification with structured JSON output</li>
                  <li>Entity extraction with confidence scores</li>
                  <li>Content generation with context awareness</li>
                  <li>Web research with real-time data</li>
                  <li>Advanced analysis with insights</li>
                  <li>Multi-node data flow validation</li>
                  <li>Database persistence verification</li>
                </ul>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
